import {produce} from 'immer'

import React, {useEffect, useRef} from 'react'

import {useAppDispatch, useAppSelector} from '../../../../../../../../../../../../hooks/react-redux'
import {useJobId} from '../../../../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {
  getDraft,
  getDraftJobs,
  isSuggestionSkipped,
  getSkippedSuggestions,
  isPipelineFormOpened,
} from '../../../../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {
  pipelineDraft,
  pipelineForm,
  suggestions,
} from '../../../../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {FormType} from '../../../../../../../../../EditPipelinePage/slices/EditPipelinePage.slices.types'
import {usePipelineId} from '../../../../../../../../../hooks/pipeline'
import SuggestionMessage from '../../../../../../../SuggestionMessage/SuggestionMessage'
import {IntegrationType} from '../../../../../Integrations/Integrations.consts'
import type {SmartDetectorProps} from '../SmartDetectors.types'

export function CliDockerLogin({name, value, stepIndex, focused}: SmartDetectorProps) {
  const jobId = useJobId()!
  const id = usePipelineId()
  const stepId = `step-${jobId}-${stepIndex + 1}`
  const stepList = useAppSelector(state => getDraftJobs(state, id)?.[jobId]?.steps)
  const skippedSuggestionList = useAppSelector(state => getSkippedSuggestions(state, stepId))
  const isSkipped = useAppSelector(state =>
    isSuggestionSkipped(state, stepId, IntegrationType.DOCKER),
  )
  const hasEnabledDockerIntegraion = useAppSelector(state => {
    const enabledIntegrations = getDraftJobs(state, id)?.[jobId]?.integrations
    if (enabledIntegrations == null || enabledIntegrations.length === 0) {
      return null
    }
    const allIntegrations = getDraft(state, id)?.integrations
    const deletedIntegrations = state.pipelines.pipelineDraft[id]?.deleted?.integrations
    return allIntegrations?.some(
      integration =>
        integration.type === IntegrationType.DOCKER &&
        enabledIntegrations.includes(integration.parameters.displayName) &&
        !deletedIntegrations?.includes(integration.id!),
    )
  })
  const isDockerFormOpened = useAppSelector(state =>
    isPipelineFormOpened(state, IntegrationType.DOCKER, FormType.PIPELINE_INTEGRATION),
  )
  const dispatch = useAppDispatch()
  const match = value.match(/^.*docker login(.*)(?:\n([\s\S]*))?$/)
  const script = match?.[2] ?? ''
  const isAddingDocker = useRef(false)
  const visible =
    !hasEnabledDockerIntegraion && !!match && !isSkipped && !isDockerFormOpened && focused

  useEffect(() => {
    if (hasEnabledDockerIntegraion && isAddingDocker.current) {
      dispatch(
        pipelineDraft.actions.setSteps({
          id,
          jobId,
          steps: produce(stepList ?? [], draft => {
            const step = draft[stepIndex]
            step[name] = script
          }),
        }),
      )
      isAddingDocker.current = false
    }
  }, [dispatch, hasEnabledDockerIntegraion, id, jobId, name, script, stepIndex, stepList])

  const username = match?.[1]?.match(/(?:-u|--username)[=\s](\S+)/)?.[1]
  return (
    <SuggestionMessage
      message={`We detected docker login. Add Docker integration instead? This will securely run 'docker login' while keeping your credentials safe.`}
      actionText="Add"
      visible={visible}
      onCancelClick={() => {
        dispatch(
          suggestions.actions.skip({
            id: stepId,
            values: produce(skippedSuggestionList ?? [], draft => {
              if (!draft.includes(IntegrationType.DOCKER)) {
                draft.push(IntegrationType.DOCKER)
              }
            }),
          }),
        )
      }}
      onActionClick={() => {
        isAddingDocker.current = true
        dispatch(
          pipelineForm.actions.open({
            id: IntegrationType.DOCKER,
            type: FormType.PIPELINE_INTEGRATION,
            data: {
              username,
            },
          }),
        )
      }}
    />
  )
}
